// @ts-strict-ignore
import { useCallback, useEffect, useRef, useState } from 'react'

const formatSeconds = (totalSeconds: number) => ({
  minutes: Math.floor(totalSeconds / 60),
  seconds: totalSeconds % 60 < 10 ? `0${totalSeconds % 60}` : totalSeconds % 60,
})

export const useMinutesSecondsTimer = (
  minutes = 0,
  seconds = 30,
  shouldStart = false,
  onExpire = () => {}
) => {
  const totalInitialSeconds = minutes * 60 + seconds
  const timeLeftRef = useRef(totalInitialSeconds)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const onExpireRef = useRef(onExpire)
  const [formattedTime, setFormattedTime] = useState(() => formatSeconds(totalInitialSeconds))

  useEffect(() => {
    onExpireRef.current = onExpire
  }, [onExpire])

  const startInterval = useCallback(() => {
    clearInterval(intervalRef.current)
    if (timeLeftRef.current <= 0) return

    intervalRef.current = setInterval(() => {
      timeLeftRef.current -= 1
      setFormattedTime(formatSeconds(timeLeftRef.current))
      if (timeLeftRef.current <= 0) {
        clearInterval(intervalRef.current)
        onExpireRef.current()
      }
    }, 1000)
  }, [])

  useEffect(() => {
    const newTotalSeconds = minutes * 60 + seconds
    timeLeftRef.current = newTotalSeconds
    setFormattedTime(formatSeconds(newTotalSeconds))
  }, [minutes, seconds])

  useEffect(() => {
    if (shouldStart) {
      startInterval()
    }

    return () => clearInterval(intervalRef.current)
  }, [shouldStart, startInterval])

  const resetTimer = useCallback(() => {
    const currentTotalSeconds = minutes * 60 + seconds
    timeLeftRef.current = currentTotalSeconds
    setFormattedTime(formatSeconds(currentTotalSeconds))
    if (shouldStart) {
      startInterval()
    } else {
      clearInterval(intervalRef.current)
    }
  }, [minutes, seconds, shouldStart, startInterval])

  return { timeLeft: timeLeftRef.current, formattedTime, resetTimer }
}
